/**
 * DiscoveryChecklist - Living checklist showing extraction status
 * Shows what's been extracted, what's missing, and what needs attention
 */

import {
  Check,
  X,
  AlertTriangle,
  Building2,
  Fingerprint,
  MapPin,
  Receipt,
  Landmark,
  Users,
  UserCheck,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from "lucide-react";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import type {
  ExtractedCompanyProfile,
  DataConflict,
} from "@shared/discovery-types";

interface DiscoveryChecklistProps {
  profile: ExtractedCompanyProfile;
  conflicts: DataConflict[];
  className?: string;
}

interface ChecklistItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  status: "complete" | "warning" | "missing";
  value?: string;
  required?: boolean;
  helpText?: string;
}

export function DiscoveryChecklist({
  profile,
  conflicts,
  className,
}: DiscoveryChecklistProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const { companyInfo, taxInfo, bankAccounts, vendors, customers } = profile;

  // Build checklist items
  const criticalItems: ChecklistItem[] = [
    {
      id: "name",
      label: "Company Name",
      icon: <Building2 className="h-4 w-4" />,
      status: companyInfo?.name ? "complete" : "missing",
      value: companyInfo?.name,
      required: true,
      helpText: "Required to create the company profile",
    },
    {
      id: "canton",
      label: "Tax Canton",
      icon: <MapPin className="h-4 w-4" />,
      status:
        taxInfo?.canton || companyInfo?.address?.canton
          ? "complete"
          : "missing",
      value: taxInfo?.canton || companyInfo?.address?.canton,
      required: true,
      helpText: "Required for tax jurisdiction and rates",
    },
  ];

  const recommendedItems: ChecklistItem[] = [
    {
      id: "uid",
      label: "Swiss UID",
      icon: <Fingerprint className="h-4 w-4" />,
      status: companyInfo?.uid ? "complete" : "warning",
      value: companyInfo?.uid,
      helpText: "Enables Zefix verification and MWST lookup",
    },
    {
      id: "legalForm",
      label: "Legal Form",
      icon: <Receipt className="h-4 w-4" />,
      status: companyInfo?.legalForm ? "complete" : "warning",
      value: companyInfo?.legalForm,
      helpText: "GmbH, AG, Einzelfirma, etc.",
    },
    {
      id: "vatNumber",
      label: "VAT Number",
      icon: <Receipt className="h-4 w-4" />,
      status:
        taxInfo?.vatNumber || taxInfo?.mwstRegistered !== undefined
          ? "complete"
          : "warning",
      value:
        taxInfo?.vatNumber ||
        (taxInfo?.mwstRegistered ? "MWST registered" : undefined),
      helpText: "For VAT declarations and deductions",
    },
  ];

  const dataItems: ChecklistItem[] = [
    {
      id: "bankAccounts",
      label: "Bank Accounts",
      icon: <Landmark className="h-4 w-4" />,
      status: (bankAccounts?.length || 0) > 0 ? "complete" : "warning",
      value:
        (bankAccounts?.length || 0) > 0
          ? `${bankAccounts?.length} account${(bankAccounts?.length || 0) !== 1 ? "s" : ""}`
          : undefined,
      helpText: "For automatic transaction imports",
    },
    {
      id: "vendors",
      label: "Vendors Discovered",
      icon: <Users className="h-4 w-4" />,
      status: (vendors?.length || 0) > 0 ? "complete" : "warning",
      value:
        (vendors?.length || 0) > 0
          ? `${vendors?.length} vendor${(vendors?.length || 0) !== 1 ? "s" : ""}`
          : undefined,
      helpText: "Expense categorization will be faster",
    },
    {
      id: "customers",
      label: "Customers Discovered",
      icon: <UserCheck className="h-4 w-4" />,
      status: (customers?.length || 0) > 0 ? "complete" : "warning",
      value:
        (customers?.length || 0) > 0
          ? `${customers?.length} customer${(customers?.length || 0) !== 1 ? "s" : ""}`
          : undefined,
      helpText: "Revenue tracking will be more accurate",
    },
  ];

  // Calculate progress
  const allItems = [...criticalItems, ...recommendedItems, ...dataItems];
  const completedCount = allItems.filter(i => i.status === "complete").length;
  const criticalComplete = criticalItems.every(i => i.status === "complete");
  const progressPercent = Math.round((completedCount / allItems.length) * 100);

  // Check for unresolved conflicts
  const unresolvedConflicts = conflicts.filter(c => !c.resolved).length;

  const renderItem = (item: ChecklistItem) => (
    <div
      key={item.id}
      className={cn(
        "flex items-center gap-3 py-2.5 px-3 rounded-lg transition-colors",
        item.status === "complete" && "bg-[var(--success)]/5",
        item.status === "warning" && "bg-[var(--warning)]/5",
        item.status === "missing" && "bg-destructive/5"
      )}
    >
      <div
        className={cn(
          "h-8 w-8 rounded-full flex items-center justify-center shrink-0",
          item.status === "complete" &&
            "bg-[var(--success)]/10 text-[var(--success)]",
          item.status === "warning" &&
            "bg-[var(--warning)]/10 text-[var(--warning)]",
          item.status === "missing" && "bg-destructive/10 text-destructive"
        )}
      >
        {item.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{item.label}</span>
          {item.required && (
            <Badge variant="outline" className="text-[10px] px-1 py-0">
              Required
            </Badge>
          )}
        </div>
        {item.value ? (
          <div className="text-sm text-muted-foreground truncate">
            {item.value}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">{item.helpText}</div>
        )}
      </div>
      <div className="shrink-0">
        {item.status === "complete" && (
          <Check className="h-5 w-5 text-[var(--success)]" />
        )}
        {item.status === "warning" && (
          <AlertTriangle className="h-5 w-5 text-[var(--warning)]" />
        )}
        {item.status === "missing" && (
          <X className="h-5 w-5 text-destructive" />
        )}
      </div>
    </div>
  );

  return (
    <Card className={cn("overflow-hidden", className)}>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-primary" />
                <div>
                  <CardTitle className="text-base">
                    Extraction Checklist
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {criticalComplete
                      ? "Ready to create company"
                      : "Some required fields are missing"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-sm font-semibold">
                    {progressPercent}%
                  </div>
                  <div className="text-xs text-muted-foreground">complete</div>
                </div>
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            {/* Progress bar */}
            <div className="space-y-2">
              <Progress value={progressPercent} className="h-2" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  {completedCount} of {allItems.length} items complete
                </span>
                {unresolvedConflicts > 0 && (
                  <span className="text-[var(--warning)]">
                    {unresolvedConflicts} conflict
                    {unresolvedConflicts !== 1 ? "s" : ""} to resolve
                  </span>
                )}
              </div>
            </div>

            {/* Critical items */}
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Required
              </h4>
              <div className="space-y-1.5">{criticalItems.map(renderItem)}</div>
            </div>

            {/* Recommended items */}
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Recommended
              </h4>
              <div className="space-y-1.5">
                {recommendedItems.map(renderItem)}
              </div>
            </div>

            {/* Data discovery items */}
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Discovered Data
              </h4>
              <div className="space-y-1.5">{dataItems.map(renderItem)}</div>
            </div>

            {/* Status message */}
            <div
              className={cn(
                "p-3 rounded-lg text-sm",
                criticalComplete
                  ? "bg-[var(--success)]/10 text-[var(--success)]"
                  : "bg-destructive/10 text-destructive"
              )}
            >
              {criticalComplete ? (
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  <span>
                    All required fields are complete. You can create this
                    company.
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <X className="h-4 w-4" />
                  <span>
                    Please provide the required fields before creating the
                    company.
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
